// Super-admin login. Same split-screen design language as klonit-dashboard
// /SignIn.js: a dark branding panel on the left with feature cards, and a
// pastel-gradient form panel on the right. The admin form is intentionally
// simpler -- single email + password, no social login, no signup link, no
// verification recovery (single static .env credential).
//
// POSTs /admin/auth/login, stores the returned admin JWT in localStorage,
// and redirects to /users (or the original protected route the user was
// trying to reach, via location.state.from).
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, TextField, Button, Alert, useTheme, CircularProgress,
    InputAdornment, IconButton,
} from '@mui/material';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import axiosInstance from '../utils/axiosInstance';
import { setSession, isAuthenticated } from '../utils/auth';

// KlonIT Logo SVG — copied from klonit-dashboard/SignIn.js verbatim so the
// brand mark is identical.
const KlonITLogo = ({ color = 'white', size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
        <path d="M8 6L16 16L8 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 6L24 16L16 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
    </svg>
);

// Palette mirrors klonit-dashboard/SignIn.js so the two screens feel like
// the same product.
const colors = {
    sidebarBg: '#1e293b',
    sidebarHover: '#313d4a',
    sidebarText: '#dee4ee',
    sidebarTextMuted: '#8a99af',
    accent: '#3b82f6',
    bgPrimary: '#f8fafc',
    bgMuted: '#f1f5f9',
    border: '#e2e8f0',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    success: '#059669',
    danger: '#dc2626',
};

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const FeatureCard = ({ icon, title, description }) => (
    <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        p: '1rem 1.25rem', bgcolor: colors.sidebarHover,
        border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px',
        transition: 'all 0.25s ease',
        '&:hover': {
            bgcolor: 'rgba(59,130,246,0.1)',
            borderColor: 'rgba(59,130,246,0.2)',
            transform: 'translateX(4px)',
        },
    }}>
        <Box sx={{
            width: 42, height: 42, bgcolor: 'rgba(59,130,246,0.15)', borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, color: colors.accent,
        }}>
            {icon}
        </Box>
        <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.sidebarText, mb: 0.25, fontFamily }}>
                {title}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: colors.sidebarTextMuted, lineHeight: 1.5, fontFamily }}>
                {description}
            </Typography>
        </Box>
    </Box>
);

const Login = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from?.pathname || '/users';

    useEffect(() => { document.title = 'Login | Klonit Super Admin'; }, []);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Already authenticated? Skip the form.
    useEffect(() => {
        if (isAuthenticated()) {
            navigate(redirectTo, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLoading) return;
        if (!email.trim() || !password) {
            setError('Email and password are required.');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.post('/admin/auth/login', {
                email: email.trim(),
                password,
            });
            const { token, email: adminEmail } = res.data || {};
            if (!token) throw new Error('No token returned.');
            setSession(token, adminEmail);
            navigate(redirectTo, { replace: true });
        } catch (err) {
            const status = err.response?.status;
            const msg = err.response?.data?.message || err.message;
            setError(status === 401 ? 'Invalid credentials.' : (msg || 'Login failed.'));
        } finally {
            setIsLoading(false);
        }
    };

    // Input field styling — copied from SignIn.js so input chrome matches.
    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '14px', fontFamily,
            bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
            border: 'none', outline: 'none',
            '& .MuiOutlinedInput-notchedOutline': { display: 'none !important' },
            '& fieldset': { display: 'none !important' },
            '&.Mui-focused': {
                bgcolor: 'rgba(255,255,255,1)',
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
        },
        '& .MuiInputBase-input': {
            fontFamily, fontSize: '0.875rem', py: '0.75rem', px: '0.5rem',
            border: 'none', outline: 'none',
            '&:focus': { outline: 'none', boxShadow: 'none' },
            '&::placeholder': { color: colors.textTertiary, opacity: 1 },
        },
        '& .MuiInputAdornment-root': { color: colors.textTertiary },
        '& .MuiInputLabel-root': { fontFamily },
        '& .MuiTextField-root': { border: 'none', outline: 'none' },
    };

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');`}</style>

            <Box sx={{ fontFamily, minHeight: '100vh', bgcolor: colors.bgPrimary, display: 'flex' }}>
                {/* ============== Left Branding Panel ============== */}
                <Box sx={{
                    display: { xs: 'none', lg: 'flex' }, width: '50%',
                    flexDirection: 'column', justifyContent: 'center',
                    p: '4rem', position: 'relative', overflow: 'hidden',
                    background: `linear-gradient(135deg, ${colors.sidebarBg} 0%, #1a2334 50%, #0f172a 100%)`,
                    '&::before': {
                        content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                        background: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.18) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(139,92,246,0.10) 0%, transparent 45%)',
                    },
                }}>
                    <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 480, mx: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.875rem', mb: 3 }}>
                            <Box sx={{
                                width: 48, height: 48, bgcolor: colors.accent, borderRadius: '12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
                            }}>
                                <KlonITLogo />
                            </Box>
                            <Typography sx={{
                                fontSize: '1.75rem', fontWeight: 700, color: '#fff',
                                letterSpacing: '-0.025em', fontFamily,
                            }}>
                                KlonIT
                            </Typography>
                        </Box>

                        <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 2, py: 0.5, bgcolor: colors.sidebarHover,
                            border: '1px solid rgba(255,255,255,0.08)', borderRadius: '100px',
                            fontSize: '0.8125rem', fontWeight: 600, color: colors.accent,
                            mb: 2, letterSpacing: '0.05em',
                        }}>
                            <BoltIcon sx={{ fontSize: 14 }} />
                            SUPER ADMIN · PRIVILEGED ACCESS
                        </Box>

                        <Typography sx={{
                            fontSize: '2.75rem', fontWeight: 700, lineHeight: 1.15,
                            letterSpacing: '-0.03em', color: '#fff', mb: 1.5, fontFamily,
                        }}>
                            Operate the<br />
                            <Box component="span" sx={{ color: colors.accent }}>Klonit platform</Box>
                        </Typography>

                        <Typography sx={{
                            fontSize: '1rem', lineHeight: 1.7, color: colors.sidebarTextMuted,
                            mb: 2.5, fontFamily,
                        }}>
                            Manage every customer account, review and settle invoices,
                            and keep Klonit's billing in good standing — all from one place.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                            <FeatureCard
                                icon={<PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />}
                                title="User control"
                                description="Suspend or restore any account in one click"
                            />
                            <FeatureCard
                                icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />}
                                title="Settle invoices"
                                description="Cash, check, or card — all routed through the same reset flow"
                            />
                            <FeatureCard
                                icon={<VerifiedUserOutlinedIcon sx={{ fontSize: 20 }} />}
                                title="Audited actions"
                                description="Every settlement records who, when, and how"
                            />
                        </Box>
                    </Box>
                </Box>

                {/* ============== Right Form Panel ============== */}
                <Box sx={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    p: { xs: 2, lg: '3rem 4rem' }, position: 'relative', overflow: 'hidden',
                    background: `linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)`,
                    '&::before': {
                        content: '""', position: 'absolute', top: '-30%', right: '-20%',
                        width: '500px', height: '500px', borderRadius: '50%', pointerEvents: 'none',
                        background: `radial-gradient(circle, ${theme.palette.primary.main}12 0%, transparent 70%)`,
                    },
                    '&::after': {
                        content: '""', position: 'absolute', bottom: '-20%', left: '-10%',
                        width: '400px', height: '400px', borderRadius: '50%', pointerEvents: 'none',
                        background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
                    },
                }}>
                    <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
                        {/* Mobile logo */}
                        <Box sx={{
                            display: { xs: 'flex', lg: 'none' }, alignItems: 'center',
                            justifyContent: 'center', gap: '0.75rem', mb: 2,
                        }}>
                            <Box sx={{
                                width: 44, height: 44, bgcolor: colors.sidebarBg, borderRadius: '12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <KlonITLogo color="white" />
                            </Box>
                            <Typography sx={{
                                fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary,
                                letterSpacing: '-0.025em', fontFamily,
                            }}>
                                KlonIT
                            </Typography>
                        </Box>

                        {/* Header */}
                        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                            <Box sx={{
                                width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 1.5,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
                            }}>
                                <LockOpenOutlinedIcon sx={{ color: '#fff', fontSize: 24 }} />
                            </Box>
                            <Typography sx={{
                                fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary,
                                letterSpacing: '-0.02em', mb: 0.25, fontFamily,
                            }}>
                                Welcome Back
                            </Typography>
                            <Typography sx={{
                                fontSize: '0.8125rem', color: colors.textTertiary, fontFamily,
                            }}>
                                Sign in to the Klonit Super Admin console
                            </Typography>
                        </Box>

                        {error && (
                            <Alert
                                severity="error"
                                sx={{
                                    mb: 1.5, borderRadius: '12px', fontFamily, fontSize: '0.8125rem',
                                    '& .MuiAlert-message': { fontFamily },
                                }}
                            >
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <TextField
                                    fullWidth size="small" type="email" placeholder="Email address"
                                    autoComplete="email" autoFocus
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required disabled={isLoading}
                                    InputProps={{
                                        startAdornment: (
                                            <EmailOutlinedIcon
                                                sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }}
                                            />
                                        ),
                                    }}
                                    sx={inputSx}
                                />
                                <TextField
                                    fullWidth size="small"
                                    type={showPwd ? 'text' : 'password'}
                                    placeholder="Password" autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required disabled={isLoading}
                                    InputProps={{
                                        startAdornment: (
                                            <LockOutlinedIcon
                                                sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }}
                                            />
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small" tabIndex={-1}
                                                    onClick={() => setShowPwd((v) => !v)}
                                                    sx={{ color: colors.textTertiary }}
                                                >
                                                    {showPwd
                                                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                                                        : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={inputSx}
                                />
                            </Box>

                            <Box sx={{ height: 12 }} />

                            <Button
                                type="submit" fullWidth disabled={isLoading}
                                sx={{
                                    py: '0.75rem', fontFamily, fontSize: '0.9375rem',
                                    fontWeight: 600, textTransform: 'none',
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                    color: '#fff', borderRadius: '14px', height: 46, gap: 1,
                                    boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                                    transition: 'all 0.25s ease',
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                        boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:disabled': { opacity: 0.7 },
                                }}
                            >
                                {isLoading && <CircularProgress size={18} sx={{ color: '#fff' }} />}
                                {isLoading ? 'Signing In...' : 'Sign In'}
                            </Button>
                        </form>

                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: 2, mt: 2,
                        }}>
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                fontSize: '0.6875rem', color: colors.textTertiary, fontFamily,
                            }}>
                                <ShieldOutlinedIcon sx={{ fontSize: 13, color: colors.success }} />
                                Secure
                            </Box>
                            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.textTertiary }} />
                            <Typography sx={{ fontSize: '0.75rem', color: colors.textTertiary, fontFamily }}>
                                Super Admin access only
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default Login;
